import React from 'react';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { ChevronLeft, ChevronDown, Folder as FolderIcon, FolderOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FileCategory, ProjectFile } from '@/types';
import {
  Folder,
  getChildFolders,
  getFilesInFolder,
  countFilesUnder,
} from './folderTypes';
import FolderDropZone from './FolderDropZone';
import DocumentTreeItem from './DocumentTreeItem';

/** Everything a node needs, threaded down the tree as one bundle. */
export interface FolderTreeShared {
  category: FileCategory;
  folders: Folder[];
  files: ProjectFile[];
  expandedFolderIds: Set<string>;
  selectedFolderId: string | null;
  selectedIds: Set<string>;
  isAndroid: boolean;
  selectionMode: boolean;
  tileHandlers: (id: string, onTap?: () => void) => Record<string, unknown>;
  onToggleExpand: (id: string) => void;
  onSelectFolder: (id: string) => void;
  onFileClick: (file: ProjectFile) => void;
  onToggleSelection: (id: string) => void;
  onDownload: (file: ProjectFile) => void;
}

interface FolderNodeProps {
  folder: Folder;
  depth: number;
  shared: FolderTreeShared;
}

export default function FolderNode({ folder, depth, shared }: FolderNodeProps) {
  const expanded = shared.expandedFolderIds.has(folder.id);
  const isSelected = shared.selectedFolderId === folder.id;
  const childFolders = getChildFolders(shared.folders, shared.category, folder.id);
  const childFiles = getFilesInFolder(shared.files, folder.id);
  const count = countFilesUnder(
    shared.folders,
    shared.files,
    shared.category,
    folder.id,
  );
  const hasChildren = childFolders.length > 0 || childFiles.length > 0;

  return (
    <Collapsible open={expanded}>
      <FolderDropZone id={folder.id} folderId={folder.id}>
        <div
          style={{ marginInlineStart: depth * 20 }}
          onClick={() => shared.onSelectFolder(folder.id)}
          onDoubleClick={() => shared.onToggleExpand(folder.id)}
          className={cn(
            'flex items-center gap-2 px-3 py-2.5 rounded-xl border cursor-pointer select-none transition-colors group',
            isSelected
              ? 'bg-primary/10 border-primary'
              : 'bg-card border-border hover:bg-secondary/50',
          )}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              shared.onToggleExpand(folder.id);
            }}
            className="flex-shrink-0 w-5 h-5 flex items-center justify-center text-muted-foreground"
            aria-label={expanded ? 'כווץ תיקייה' : 'הרחב תיקייה'}
          >
            {expanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronLeft className="w-4 h-4" />
            )}
          </button>

          <div className={cn('flex-shrink-0', isSelected ? 'text-primary' : 'text-muted-foreground')}>
            {expanded ? <FolderOpen className="w-5 h-5" /> : <FolderIcon className="w-5 h-5" />}
          </div>

          <span className="flex-1 min-w-0 truncate font-medium text-sm">{folder.name}</span>

          {count > 0 && (
            <span className="flex-shrink-0 text-xs text-muted-foreground bg-secondary rounded-full px-2 py-0.5">
              {count}
            </span>
          )}
        </div>
      </FolderDropZone>

      <CollapsibleContent className="overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up">
        <div className="mt-1 space-y-1">
          {childFolders.map((child) => (
            <FolderNode key={child.id} folder={child} depth={depth + 1} shared={shared} />
          ))}
          {childFiles.map((file) => (
            <DocumentTreeItem
              key={file.id}
              file={file}
              depth={depth + 1}
              isSelected={shared.selectedIds.has(file.id)}
              isAndroid={shared.isAndroid}
              selectionMode={shared.selectionMode}
              tileHandlers={shared.tileHandlers}
              onFileClick={shared.onFileClick}
              onToggleSelection={shared.onToggleSelection}
              onDownload={shared.onDownload}
            />
          ))}
          {expanded && !hasChildren && (
            <p
              style={{ marginInlineStart: (depth + 1) * 20 }}
              className="text-xs text-muted-foreground px-3 py-2"
            >
              תיקייה ריקה
            </p>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
